/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { css } from 'styled-components';
import { Cpds } from '@Cpds.Ui';
import {
  CpdsMagazineNameplateStyleTag,
  CpdsMagazineNameplateTitle,
  CpdsMagazineNameplateHeading,
  CpdsMagazineNameplateDescription,
  CpdsMagazineNameplateContent,
  CpdsMagazineNameplateLogo,
  CpdsMagazineNameplateLogoContainer,
} from './styles/cpds-magazine-nameplate-style';
import { themeToken } from '../../cpds/utils/cpds-utils';
import { CpdsMagazineNameplateProps } from './typings';
import { color } from '../token/shared/common';

export enum NameplateType {
  STANDARD = 'STANDARD',
  PARTNERSHIP = 'PARTNERSHIP',
}

export enum HeadingAlignType {
  LEFT = 'LEFT',
  CENTER = 'CENTER',
}

export enum ColorType {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  TERTIARY = 'TERTIARY',
}

export enum ImageSizeType {
  LARGE = 'LARGE',
  SMALL = 'SMALL',
}

const CpdsMagazineNameplate: React.FC<CpdsMagazineNameplateProps> = (props) => {
  const { domRef, exClasses, children, image, title, cta } = props;
  const imageOnly = !title && !children && !cta && !cta?.text && !cta?.href && image && image?.url;
  
  /* Important : By default html tag is <div></div> 
  but you can change that into suitable html element base on your requirements*/

  return (
    <CpdsMagazineNameplateStyleTag className={`cpds-magazine-nameplate ${exClasses}`} ref={domRef} {...props}>
      <CpdsMagazineNameplateHeading {...props}>
        {image && (
          <CpdsMagazineNameplateLogoContainer {...{ ...props, imageOnly }}>
            <CpdsMagazineNameplateLogo src={image?.url} alt="partent-logo" />
          </CpdsMagazineNameplateLogoContainer>
        )}
        {title && <CpdsMagazineNameplateTitle {...props} className='cpds-display-roman-book-01'>{title}</CpdsMagazineNameplateTitle>}
      </CpdsMagazineNameplateHeading>
      <CpdsMagazineNameplateDescription {...props}>
        {children && <CpdsMagazineNameplateContent {...props} className='cpds-meta-roman-book-09'>{children}</CpdsMagazineNameplateContent>}
        {cta?.text && cta?.href && (
          <Cpds.Button
            buttonType={cta?.buttonType || ""}
            isDisabled={cta?.isDisabled}
            domRef={cta?.domRef}
            themeColorMode={cta?.themeColorMode}
            themeTypography={cta?.themeTypography}
            tag={cta?.tag || "a"}
            exClasses={cta.exClasses || ""}
            href={cta?.href}
            styleOverrides={css`
              &:focus {
                  outline: none;
                  border: none;
              }
              margin-top: 12px;
              letter-spacing: 0.06px;
              svg{
                height: 8px;
                width: 5px;
              }
              ${({
                theme: {
                  styles: { cpds },
                },
                themeColorMode
              }) => {
                const { token } = themeToken(cpds, cpds?.themeColorMode, themeColorMode);
                switch (props.colorType) {
                  case ColorType.PRIMARY:
                    return css`
                      color: ${token?.magazine_nameplate_standard_primary_content_color || color.cpds_neutral_900};
                    `;
                  case ColorType.SECONDARY:
                    return css`
                      color: ${token?.magazine_nameplate_standard_secondary_content_color || color.cpds_neutral_900};
                    `;
                  case ColorType.TERTIARY:
                    return css`
                      color: ${props.nameplateType === NameplateType.PARTNERSHIP
                        ? token?.magazine_nameplate_partnership_tertiary_content_color
                        : token?.magazine_nameplate_standard_tertiary_content_color};
                    `;
                  default:
                    return css`
                      color: ${token?.magazine_nameplate_standard_primary_content_color || color.cpds_neutral_900};
                    `;
                }
              }}
              span svg path {
                ${({
                  theme: {
                    styles: { cpds },
                  },
                  themeColorMode
                }) => {
                  const { token } = themeToken(cpds, cpds?.themeColorMode, themeColorMode);
                  switch (props.colorType) {
                    case ColorType.PRIMARY:
                      return css`
                        fill: ${token?.magazine_nameplate_standard_primary_content_color || color.cpds_neutral_900};
                      `;
                    case ColorType.SECONDARY:
                      return css`
                        fill: ${token?.magazine_nameplate_standard_secondary_content_color || color.cpds_neutral_900};
                      `;
                    case ColorType.TERTIARY:
                      return css`
                        fill: ${props.nameplateType === NameplateType.PARTNERSHIP
                          ? token?.magazine_nameplate_partnership_tertiary_content_color
                          : token?.magazine_nameplate_standard_tertiary_content_color};
                      `;
                    default:
                      return css`
                        fill: ${token?.magazine_nameplate_standard_primary_content_color || color.cpds_neutral_900};
                      `;
                  }
                }}
              }
            `}
          >
            {cta?.text}
            <Cpds.Icon id={'key-1'} name="chevron-right" size="extra_small" />
          </Cpds.Button>
        )}
      </CpdsMagazineNameplateDescription>
    </CpdsMagazineNameplateStyleTag>
  );
};

export default CpdsMagazineNameplate;
